import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { isEmptyValue } from "../../../utils/formValueUtils";
import { getDateFormat, parseDateSafely } from "./dateValidatorUtils";

/**
 * Helper function to get value codes for a specific label from field schema
 */
var getValueCodesForLabel = function getValueCodesForLabel(fieldSchemas, fieldName, labelToFind) {
  var _field$metadata;
  var field = fieldSchemas.find(function (f) {
    return f.field_name === fieldName;
  });
  if (!field || !((_field$metadata = field.metadata) !== null && _field$metadata !== void 0 && _field$metadata.valid_values)) {
    return [];
  }
  var matchingValues = field.metadata.valid_values.filter(function (option) {
    return option.label === labelToFind;
  }).map(function (option) {
    return String(option.value);
  });
  return matchingValues;
};

/**
 * Evaluates a specific cross-field rule based on rule_id
 */
var evaluateCrossFieldRule = function evaluateCrossFieldRule(rule, formData, fieldSchemas) {
  var rule_id = rule.rule_id,
    fields = rule.fields;

  // Extract field values
  var fieldValues = fields.map(function (fieldName) {
    return formData[fieldName];
  });

  // Date comparison rules
  if (rule_id === "dob_before_dod") {
    // Date_Of_Birth must be on or before Date_Of_Death
    var _fieldValues = _slicedToArray(fieldValues, 2),
      dob = _fieldValues[0],
      dod = _fieldValues[1];

    // Skip validation if either field is empty
    if (isEmptyValue(dob) || isEmptyValue(dod)) {
      return {
        isValid: true
      };
    }

    // Parse dates using the correct format from field schema
    var dobFormat = getDateFormat(fieldSchemas, fields[0]);
    var dodFormat = getDateFormat(fieldSchemas, fields[1]);
    var dobDate = parseDateSafely(dob, dobFormat);
    var dodDate = parseDateSafely(dod, dodFormat);

    // Skip validation if either date is invalid
    if (!dobDate || !dodDate) {
      return {
        isValid: true
      };
    }
    if (dobDate > dodDate) {
      return {
        isValid: false,
        errorField: fields[0]
      }; // Show error on Date_Of_Birth
    }
    return {
      isValid: true
    };
  }
  if (rule_id === "exam_date_after_dob") {
    // Exam_Date must be after Date_Of_Birth
    var _fieldValues2 = _slicedToArray(fieldValues, 2),
      examDate = _fieldValues2[0],
      _dob = _fieldValues2[1];

    // Skip validation if either field is empty
    if (isEmptyValue(examDate) || isEmptyValue(_dob)) {
      return {
        isValid: true
      };
    }

    // Parse dates using the correct format from field schema
    var examDateFormat = getDateFormat(fieldSchemas, fields[0]);
    var _dobFormat = getDateFormat(fieldSchemas, fields[1]);
    var examDateObj = parseDateSafely(examDate, examDateFormat);
    var _dobDate = parseDateSafely(_dob, _dobFormat);

    // Skip validation if either date is invalid
    if (!examDateObj || !_dobDate) {
      return {
        isValid: true
      };
    }
    if (examDateObj <= _dobDate) {
      return {
        isValid: false,
        errorField: fields[0]
      }; // Show error on Exam_Date
    }
    return {
      isValid: true
    };
  }
  if (rule_id === "exam_date_before_dod") {
    // Exam_Date must be on or before Date_Of_Death (when set)
    var _fieldValues3 = _slicedToArray(fieldValues, 2),
      _examDate = _fieldValues3[0],
      _dod = _fieldValues3[1];

    // Skip validation if exam date is empty or DOD is not set
    if (isEmptyValue(_examDate) || isEmptyValue(_dod)) {
      return {
        isValid: true
      };
    }

    // Parse dates using the correct format from field schema
    var _examDateFormat = getDateFormat(fieldSchemas, fields[0]);
    var _dodFormat = getDateFormat(fieldSchemas, fields[1]);
    var _examDateObj = parseDateSafely(_examDate, _examDateFormat);
    var _dodDate = parseDateSafely(_dod, _dodFormat);

    // Skip validation if either date is invalid
    if (!_examDateObj || !_dodDate) {
      return {
        isValid: true
      };
    }
    if (_examDateObj > _dodDate) {
      return {
        isValid: false,
        errorField: fields[0]
      }; // Show error on Exam_Date
    }
    return {
      isValid: true
    };
  }
  if (rule_id === "followup_date_after_exam") {
    // Date_Of_Follow_Up cannot be before Exam_Date
    var _fieldValues4 = _slicedToArray(fieldValues, 2),
      followupDate = _fieldValues4[0],
      _examDate2 = _fieldValues4[1];

    // Skip validation if either field is empty
    if (isEmptyValue(followupDate) || isEmptyValue(_examDate2)) {
      return {
        isValid: true
      };
    }

    // Parse dates using the correct format from field schema
    var followupDateFormat = getDateFormat(fieldSchemas, fields[0]);
    var _examDateFormat2 = getDateFormat(fieldSchemas, fields[1]);
    var followupDateObj = parseDateSafely(followupDate, followupDateFormat);
    var _examDateObj2 = parseDateSafely(_examDate2, _examDateFormat2);

    // Skip validation if either date is invalid
    if (!followupDateObj || !_examDateObj2) {
      return {
        isValid: true
      };
    }
    if (followupDateObj < _examDateObj2) {
      return {
        isValid: false,
        errorField: fields[0]
      }; // Show error on Date_Of_Follow_Up
    }
    return {
      isValid: true
    };
  }
  if (rule_id === "followup_date_before_dod") {
    // Date_Of_Follow_Up cannot be after Date_Of_Death (when set)
    var _fieldValues5 = _slicedToArray(fieldValues, 2),
      _followupDate = _fieldValues5[0],
      _dod2 = _fieldValues5[1];

    // Skip validation if follow-up date is empty or DOD is not set
    if (isEmptyValue(_followupDate) || isEmptyValue(_dod2)) {
      return {
        isValid: true
      };
    }

    // Parse dates using the correct format from field schema
    var _followupDateFormat = getDateFormat(fieldSchemas, fields[0]);
    var _dodFormat2 = getDateFormat(fieldSchemas, fields[1]);
    var _followupDateObj = parseDateSafely(_followupDate, _followupDateFormat);
    var _dodDate2 = parseDateSafely(_dod2, _dodFormat2);

    // Skip validation if either date is invalid
    if (!_followupDateObj || !_dodDate2) {
      return {
        isValid: true
      };
    }
    if (_followupDateObj > _dodDate2) {
      return {
        isValid: false,
        errorField: fields[0]
      }; // Show error on Date_Of_Follow_Up
    }
    return {
      isValid: true
    };
  }
  if (rule_id === "ethnicity_hispanic_race") {
    // Patient_Ethnicity cannot be 'Not Hispanic or Latino' or 'Unknown'
    // when Patient_Race includes 'Hispanic or Latino'
    var _fieldValues6 = _slicedToArray(fieldValues, 2),
      patientRace = _fieldValues6[0],
      patientEthnicity = _fieldValues6[1];

    // Skip validation if either field is empty
    if (isEmptyValue(patientRace) || isEmptyValue(patientEthnicity)) {
      return {
        isValid: true
      };
    }

    // Get value codes dynamically from field schemas
    var hispanicRaceCodes = getValueCodesForLabel(fieldSchemas, fields[0],
    // Patient_Race field
    "Hispanic or Latino");
    var notHispanicEthnicityCodes = getValueCodesForLabel(fieldSchemas, fields[1],
    // Patient_Ethnicity field
    "Not Hispanic or Latino");
    var unknownEthnicityCodes = getValueCodesForLabel(fieldSchemas, fields[1],
    // Patient_Ethnicity field
    "Unknown");

    // Check if race includes Hispanic or Latino
    var raceArray = Array.isArray(patientRace) ? patientRace : [patientRace];
    var hasHispanicRace = raceArray.some(function (race) {
      var raceStr = String(race);
      // Check against dynamically loaded codes from field schema
      return hispanicRaceCodes.includes(raceStr);
    });
    if (hasHispanicRace) {
      // Check if ethnicity is invalid using dynamically loaded codes
      var invalidEthnicityCodes = [].concat(_toConsumableArray(notHispanicEthnicityCodes), _toConsumableArray(unknownEthnicityCodes));
      var isInvalid = invalidEthnicityCodes.includes(String(patientEthnicity));
      if (isInvalid) {
        return {
          isValid: false,
          errorField: fields[1]
        }; // Show error on Patient_Ethnicity
      }
    }
    return {
      isValid: true
    };
  }

  // Default: rule not implemented, skip validation
  return {
    isValid: true
  };
};

/**
 * Evaluates all cross-field rules and returns FieldError objects
 */
export var evaluateCrossFieldRules = function evaluateCrossFieldRules(formData, crossFieldRules, fieldSchemas) {
  var errors = [];
  if (!crossFieldRules || crossFieldRules.length === 0) {
    return errors;
  }
  crossFieldRules.forEach(function (rule) {
    var result = evaluateCrossFieldRule(rule, formData, fieldSchemas);
    if (!result.isValid && result.errorField) {
      errors.push({
        field: result.errorField,
        error: rule.message,
        type: "cross_field"
      });
    }
  });
  return errors;
};

/**
 * Get all fields that are part of cross-field rules
 */
export var getCrossFieldRuleFields = function getCrossFieldRuleFields(crossFieldRules) {
  var fields = new Set();
  if (!crossFieldRules || crossFieldRules.length === 0) {
    return fields;
  }
  crossFieldRules.forEach(function (rule) {
    rule.fields.forEach(function (fieldName) {
      fields.add(fieldName);
    });
  });
  return fields;
};

/**
 * Get all cross-field rules that involve a specific field
 */
export var getCrossFieldRulesForField = function getCrossFieldRulesForField(fieldName, crossFieldRules) {
  if (!crossFieldRules || crossFieldRules.length === 0) {
    return [];
  }
  return crossFieldRules.filter(function (rule) {
    return rule.fields.includes(fieldName);
  });
};